import { useState, useEffect, useCallback } from 'react';
import type { ILabel, IWorkflowStatus } from '@zappar/zw-api-client';
import type { ZapworksProvider } from '../providers/zapworks/ZapworksProvider';
import { mediaLibraryStore } from '../mediaLibraryStore';
import { useMediaLibraryStore } from './useMediaLibraryStore';

// ==================== Labels ====================

// Module-level caches shared across all hook instances.
// Keyed by workspaceId. labelsPromiseCache deduplicates concurrent in-flight requests.
const labelsCache = new Map<string, ILabel[]>();
const labelsPromiseCache = new Map<string, Promise<ILabel[]>>();
const EMPTY_LABELS: ILabel[] = [];

function setWorkspaceLabels(workspaceId: string, next: ILabel[]) {
  labelsCache.set(workspaceId, next);
  mediaLibraryStore.setState({
    labelsByWorkspace: { ...mediaLibraryStore.getState().labelsByWorkspace, [workspaceId]: next },
  });
}

interface UseLabelsResult {
  labels: ILabel[];
  loading: boolean;
  error: string | undefined;
  createLabel: (data: Omit<ILabel, 'uid' | 'workspaceId'>) => Promise<ILabel>;
  updateLabel: (uid: string, data: Partial<Pick<ILabel, 'name' | 'color'>>) => Promise<ILabel>;
  deleteLabel: (uid: string) => Promise<void>;
  getLabelNameByUid: (uid: string) => string;
  getLabelObjectByUid: (uid: string) => ILabel | undefined;
}

export function useLabels(provider: ZapworksProvider | undefined, workspaceId: string): UseLabelsResult {
  const labels = useMediaLibraryStore(s => s.labelsByWorkspace[workspaceId] ?? EMPTY_LABELS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!provider || !workspaceId) return;

    // Serve from cache — no fetch needed
    if (labelsCache.has(workspaceId)) return;

    setLoading(true);
    setError(undefined);

    // Reuse an in-flight request if another instance already started one
    let req = labelsPromiseCache.get(workspaceId);
    if (!req) {
      req = provider.getLabels(workspaceId);
      labelsPromiseCache.set(workspaceId, req);
    }

    req
      .then((data) => setWorkspaceLabels(workspaceId, data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load labels'))
      .finally(() => {
        labelsPromiseCache.delete(workspaceId);
        setLoading(false);
      });
  }, [provider, workspaceId]);

  const createLabel = useCallback(async (data: Omit<ILabel, 'uid' | 'workspaceId'>) => {
    const created = await provider!.createLabel(workspaceId, data.name, data.color);
    setWorkspaceLabels(workspaceId, [...(labelsCache.get(workspaceId) ?? []), created]);
    return created;
  }, [provider, workspaceId]);

  const updateLabel = useCallback(async (uid: string, data: Partial<Pick<ILabel, 'name' | 'color'>>) => {
    const updated = await provider!.updateLabel(workspaceId, uid, data.name || '', data.color || '');
    setWorkspaceLabels(workspaceId, (labelsCache.get(workspaceId) ?? []).map((l) => l.uid === uid ? updated : l));
    return updated;
  }, [provider, workspaceId]);

  const deleteLabel = useCallback(async (uid: string) => {
    await provider!.deleteLabel(workspaceId, uid);
    setWorkspaceLabels(workspaceId, (labelsCache.get(workspaceId) ?? []).filter((l) => l.uid !== uid));
  }, [provider, workspaceId]);

  const getLabelNameByUid = useCallback((uid: string) => {
    const label = labels.find((l) => l.uid === uid);
    return label ? label.name : 'Unknown';
  }, [labels]);

  const getLabelObjectByUid = useCallback((uid: string) => {
    return labels.find((l) => l.uid === uid);
  }, [labels]);


  return { labels, loading, error, createLabel, updateLabel, deleteLabel, getLabelNameByUid, getLabelObjectByUid };
}

// ==================== Workflow Statuses ====================

const statusesCache = new Map<string, IWorkflowStatus[]>();
const statusesPromiseCache = new Map<string, Promise<IWorkflowStatus[]>>();

interface UseWorkflowStatusesResult {
  statuses: IWorkflowStatus[];
  loading: boolean;
  error: string | undefined;
  getStatusNameByUid: (uid: string) => string;
}

export function useWorkflowStatuses(provider: ZapworksProvider | undefined, workspaceId: string): UseWorkflowStatusesResult {
  const [statuses, setStatuses] = useState<IWorkflowStatus[]>(() => statusesCache.get(workspaceId) ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!provider || !workspaceId) return;

    if (statusesCache.has(workspaceId)) {
      setStatuses(statusesCache.get(workspaceId)!);
      return;
    }

    setLoading(true);
    setError(undefined);

    let req = statusesPromiseCache.get(workspaceId);
    if (!req) {
      req = provider.listWorkflowStatus(workspaceId).then((response) => response.results.flat());
      statusesPromiseCache.set(workspaceId, req);
    }

    req
      .then((data) => {
        statusesCache.set(workspaceId, data);
        setStatuses(data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load workflow statuses'))
      .finally(() => {
        statusesPromiseCache.delete(workspaceId);
        setLoading(false);
      });
  }, [provider, workspaceId]);

  const getStatusNameByUid = useCallback((uid: string) => {
    const status = statuses.find((s) => s.uid === uid);
    return status ? status.name : 'Unknown';
  }, [statuses]);

  return { statuses, loading, error, getStatusNameByUid };
}
